import * as React from 'react';
import useAsyncEffect from '../../../hooks/useAsyncEffect';
import useIsMount from '../../../hooks/useIsMount';
import {
    createSubscription,
    createSubscriptionIntent,
    getStripe,
    PAYMENT_SECRET_URL_PARAM,
    PAYMENT_TIER_URL_PARAM,
    PAYMENT_ID_URL_PARAM,
    PAYMENT_REDIRECT_STATUS_URL_PARAM
} from '../../../modules/Subscription';
import logger from '../../../modules/logging/logger';
import { PLANS } from '../../../lib/Plans';
import { getStudioUrl } from '../../../modules/router.utils';
import InlineLoader from '../InlineLoader';
import CheckoutForm from '../CheckoutForm/CheckoutForm';
import PlanCard from '../PlanCard/PlanCard';
import type { ThemeCard } from '../../../types/Wizard';
import { useAppDispatch, useAppSelector } from '../../../store/app';
import { setUrlParams } from '../../../actions/global';
import { addToast, removeToastById } from '../../../actions/global/toasts';
import useUpdateRef from '../../../hooks/useUpdateRef';
import themeCards from '../../../modules/cards/theme-cards.json';
import cn from '~modules/cn';
import CheckIcon from '../../../assets/icons/v2design/basics/check.svg';

import './subscribe-plan.scss';

type Props = {
    tierId: string;
    projectId: string;
    themeId?: string;
    onPaymentSuccess?: () => void;
};

const ERROR_TOAST_ID = 'SUBSCRIBE_PLAN_ERROR_TOAST';

const getKeyName = (projectId: string, tierId: string) => `stackbit.subscriptionKey.${projectId}.${tierId}`;

const getCachedSubscription = (projectId: string, tierId: string) => {
    try {
        const obj = JSON.parse(window.localStorage.getItem(getKeyName(projectId, tierId))!);
        return {
            key: obj.key,
            amount: obj.amount,
            date: obj.date,
            id: obj.id
        };
    } catch (e) {
        return {};
    }
};

const setCachedSubscription = ({ projectId, tierId, key, amount, id }: { projectId: string; tierId: string; key: string; amount: number; id: string }) => {
    window.localStorage.setItem(getKeyName(projectId, tierId), JSON.stringify({ key, amount, id, date: Date.now() }));
};

export const removeCachedSubscription = (projectId: string, tierId: string) => {
    window.localStorage.removeItem(getKeyName(projectId, tierId));
};

const SubscribePlan = (props: Props) => {
    const { tierId, projectId } = props;
    const plan = PLANS[tierId];
    const onPaymentSuccessRef = useUpdateRef(props.onPaymentSuccess);

    const isMounted = useIsMount();

    const urlParams = useAppSelector((state) => state.global.urlParams);
    const dispatch = useAppDispatch();

    const [subscription, setSubscription] = React.useState<{ key: string; amount: number | undefined; id: string }>(() => {
        const { key, date, amount, id } = getCachedSubscription(projectId, tierId);
        if (!key || !id) {
            return { key: '', amount: undefined, id: '' };
        }
        if (Date.now() - date > 1000 * 60 * 60 * 12) {
            removeCachedSubscription(projectId, tierId);
            return { key: '', amount: undefined, id: '' };
        }
        return { key, amount, id };
    });
    const [isLoading, setLoading] = React.useState(true);
    const [isSuccess, setIsSuccess] = React.useState(false);

    const planAmount = typeof subscription.amount === 'number' ? subscription.amount : plan?.defaultPrice;
    const themeName = (themeCards as unknown as ThemeCard[]).find(({ id }) => id === props.themeId)?.title ?? '';

    const displayError = React.useCallback(
        (label: string, showButton = true, disappear?: number) => {
            dispatch(
                addToast({
                    type: 'error',
                    label: label || 'There were error processing your payment',
                    id: ERROR_TOAST_ID,
                    buttonTitle: showButton ? 'Try again' : undefined,
                    disappear,
                    onButtonClick() {
                        return true;
                    }
                })
            );
        },
        [dispatch]
    );

    const onSuccess = React.useCallback(() => {
        setIsSuccess(true);
        removeCachedSubscription(projectId, tierId);
        onPaymentSuccessRef.current?.();
    }, [onPaymentSuccessRef, projectId, tierId]);

    useAsyncEffect(async () => {
        const paymentSecret = urlParams[PAYMENT_SECRET_URL_PARAM];
        try {
            if (paymentSecret) {
                // was redirected to this page from 3rd party payment page, like 3ds of the bank etc
                const stripe = await getStripe();
                let paymentIntentResult;
                try {
                    paymentIntentResult = await stripe!.retrievePaymentIntent(paymentSecret);
                } catch (err) {
                    logger.error('Error verifying payment intent', err);
                }

                switch (paymentIntentResult?.paymentIntent?.status) {
                    case 'succeeded':
                        onSuccess();
                        break;
                    case 'requires_payment_method':
                        logger.error('Error verifying payment intent not successful', paymentIntentResult);
                        displayError('Your payment was not successful');
                        break;
                    default:
                        logger.error('Error verifying payment intent custom message', paymentIntentResult);
                        displayError('There were error processing your payment');
                        break;
                }

                setLoading(false);

                const newUrlParams = { ...urlParams };

                delete newUrlParams[PAYMENT_SECRET_URL_PARAM];
                delete newUrlParams[PAYMENT_TIER_URL_PARAM];
                delete newUrlParams[PAYMENT_ID_URL_PARAM];
                delete newUrlParams[PAYMENT_REDIRECT_STATUS_URL_PARAM];

                dispatch(setUrlParams({ urlParams: newUrlParams }));

                return;
            }

            if (!subscription.key) {
                const subscriptionIntent = await createSubscriptionIntent({ projectId: props.projectId, tierId });
                setSubscription({
                    // @ts-expect-error TS(2571): Object is of type 'unknown'.
                    key: subscriptionIntent.clientSecret,
                    // @ts-expect-error TS(2571): Object is of type 'unknown'.
                    amount: subscriptionIntent.amount,
                    // @ts-expect-error TS(2571): Object is of type 'unknown'.
                    id: subscriptionIntent.subscriptionId
                });
                setCachedSubscription({
                    projectId,
                    tierId,
                    // @ts-expect-error TS(2571): Object is of type 'unknown'.
                    key: subscriptionIntent.clientSecret,
                    // @ts-expect-error TS(2571): Object is of type 'unknown'.
                    amount: subscriptionIntent.amount,
                    // @ts-expect-error TS(2571): Object is of type 'unknown'.
                    id: subscriptionIntent.subscriptionId
                });
            }

            setLoading(false);
        } catch (err) {
            logger.error(paymentSecret ? 'Error verifying payment intent' : 'Error creating payment intent', err);
            if (isMounted()) {
                createSubscription({
                    projectId,
                    tierId,
                    cancelUrl: window.location.href,
                    successUrl: getStudioUrl(projectId, true)
                });
            }
        }
    }, []);

    return (
        <div className="subscribe-plan">
            <div className="subscribe-plan__card-column">
                <PlanCard amount={planAmount} tierId={tierId} themeName={themeName} />
            </div>
            <div className={cn('subscribe-plan__payment-container', { 'subscribe-plan__payment-container--success': isSuccess })}>
                {isLoading && <InlineLoader color="multipass" />}
                {subscription.key && !isSuccess && (
                    <CheckoutForm
                        projectId={projectId}
                        tierId={tierId}
                        clientSecret={subscription.key}
                        subscriptionId={subscription.id}
                        onPaymentSuccess={onSuccess}
                        onPaymentFailure={displayError}
                        onPaymentStart={() => {
                            dispatch(removeToastById(ERROR_TOAST_ID));
                        }}
                        onPromotionCodeUpdate={(status, amount) => {
                            if (status === 'start') {
                                dispatch(removeToastById(ERROR_TOAST_ID));
                            } else if (status === 'fail') {
                                displayError('Promotion code is not valid', false, 3000);
                            } else if (status === 'success') {
                                dispatch(
                                    addToast({
                                        type: 'success',
                                        label: 'Successfully applied promotion code',
                                        disappear: 3000
                                    })
                                );
                            }
                            if (typeof amount === 'number') {
                                setSubscription({
                                    key: subscription.key,
                                    id: subscription.id,
                                    amount
                                });
                                setCachedSubscription({ projectId, tierId, key: subscription.key, amount, id: subscription.id });
                            }
                        }}
                    />
                )}
                {isSuccess && (
                    <div className="subscribe-plan__success-box">
                        <div className="subscribe-plan__success-box-icon">
                            <CheckIcon />
                        </div>
                        <div className="subscribe-plan__success-box-title">Payment successful</div>
                        <div>
                            You are now on the <strong>{plan?.title}</strong> plan
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SubscribePlan;
